"use client";

import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Checkbox,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  ClickAwayListener,
  ListItemButton,
} from "@mui/material";

import { useGeneralApiCall } from "@/services/useGeneralApiCall";

// Define the Tag interface
interface Tag {
  tag_id: string;
  name: string;
}

interface TagSelectorProps {
  selectedTags: string[];
  setSelectedTags: React.Dispatch<React.SetStateAction<string[]>>;
}

export default function TagSelector({
  selectedTags,
  setSelectedTags,
}: TagSelectorProps) {
  const { getApi } = useGeneralApiCall();
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showList, setShowList] = useState<boolean>(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(0);

  const filteredTags = allTags.filter((tag) =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggle = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]
    );
  };

  const handleDelete = (tagId: string) => {
    setSelectedTags((prev) => prev.filter((t) => t !== tagId));
  };

  const handleAddNew = () => {
    const newName = searchTerm.trim();
    if (!newName) return;

    const existingTag = allTags.find(
      (tag) => tag.name.toLowerCase() === newName.toLowerCase()
    );

    let newTagId;
    if (!existingTag) {
      newTagId = `TAG-${Date.now()}`;
      const newTag = { tag_id: newTagId, name: newName };
      setAllTags((prev) => [...prev, newTag]);
    } else {
      newTagId = existingTag.tag_id;
    }

    if (!selectedTags.includes(newTagId)) {
      setSelectedTags((prev) => [...prev, newTagId]);
    }

    setSearchTerm("");
    setShowList(false);
    setHighlightIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredTags.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (showList && filteredTags[highlightIndex]) {
        handleToggle(filteredTags[highlightIndex].tag_id);
        setSearchTerm("");
        setHighlightIndex(0);
      } else {
        handleAddNew();
      }
    } else if (e.key === "Escape") {
      setShowList(false);
    }
  };
  const fetchtags = async () => {
    const response: { data: Tag[] } = await getApi(
      "/api/style/v1/get-all-tags/"
    );
    setAllTags(response.data);
  };

  useEffect(() => {
    fetchtags();
  }, []);
  return (
    <ClickAwayListener onClickAway={() => setShowList(false)}>
      <Box sx={{ py: 2 }}>
        <Box
          sx={{
            fontSize: 30,
          }}
        >
          tags
        </Box>

        {/* Selected Tags */}
        <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {selectedTags.map((tagId) => {
            const tag = allTags.find((t) => t.tag_id === tagId);
            return (
              tag && (
                <Chip
                  key={tag.tag_id}
                  label={tag.name}
                  onDelete={() => handleDelete(tag.tag_id)}
                  variant="outlined"
                  sx={{ fontFamily: "'Yotter', sans-serif", fontSize: 20 }}
                />
              )
            );
          })}
        </Box>

        {/* Search Input */}
        <TextField
          placeholder="search and select tags..."
          fullWidth
          variant="outlined"
          value={searchTerm}
          onFocus={() => setShowList(true)}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setHighlightIndex(0);
          }}
          onKeyDown={handleKeyDown}
          sx={{
            mb: 1,
            "& .MuiInputBase-input": {
              fontFamily: "sans-serif",
              "&::placeholder": {
                color: "black",
                opacity: 1,
              },
            },
            bgcolor: "#fff",
          }}
        />

        {/* Tag List */}
        {showList && (
          <Paper variant="outlined" sx={{ maxHeight: 250, overflow: "auto" }}>
            <List dense>
              {filteredTags.map((tag, index) => (
                <ListItem
                  key={tag.tag_id}
                  disablePadding
                  sx={{
                    backgroundColor:
                      index === highlightIndex
                        ? "rgba(0,0,0,0.08)"
                        : "transparent",
                  }}
                >
                  <ListItemButton
                    selected={index === highlightIndex}
                    onClick={() => handleToggle(tag.tag_id)}
                  >
                    <ListItemIcon>
                      <Checkbox checked={selectedTags.includes(tag.tag_id)} />
                    </ListItemIcon>
                    <ListItemText primary={tag.name} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
}
